import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import MigrationPage from "./MigrationPage";
import background from "./images/background.png";

const API = process.env.REACT_APP_API_URL || "";

export default function AuthPage() {
  const [mode, setMode] = useState("login"); // "login" | "signup" | "forgot"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingMigration, setPendingMigration] = useState(null);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotNoEmail, setForgotNoEmail] = useState(false);
  const { login, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn && !pendingMigration) {
      navigate("/perfil");
    }
  }, [isLoggedIn, pendingMigration]);

  const switchMode = (newMode) => {
    setMode(newMode);
    setError("");
    setForgotSent(false);
    setForgotNoEmail(false);
    setUsername("");
    setPassword("");
  };

  const handleSubmit = async () => {
    setError("");

    if (mode === "forgot") {
      if (!username.trim()) {
        setError("Escribe tu username bb");
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: username.trim() }),
        });
        const data = await res.json();
        if (data.noEmail) {
          setForgotNoEmail(true);
        } else if (!res.ok) {
          setError(data.error || "Algo salió mal");
          return;
        }
        setForgotSent(true);
      } catch {
        setError("Error de conexión, intenta de nuevo");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!username.trim() || !password.trim()) {
      setError("Username y contraseña son requeridos bb");
      return;
    }
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (mode === "signup") {
      const usernameRegex = /^[a-zA-Z0-9_.]+$/;
      if (!usernameRegex.test(username.trim())) {
        setError("Username: solo letras, números, _ y .");
        return;
      }
    }

    setLoading(true);
    try {
      const endpoint =
        mode === "login" ? "/api/auth/login" : "/api/auth/register";
      const body =
        mode === "login"
          ? { username: username.trim(), password }
          : {
              username: username.trim(),
              password,
              email: email.trim() || undefined,
            };

      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Algo salió mal");
        return;
      }

      login({ id: data.id, username: data.username }, data.token);

      const quotesPoints = parseInt(
        localStorage.getItem("karime_game_high_score_v1") || "0"
      );
      const sopasPoints = parseInt(
        localStorage.getItem("kk_sopas_completadas_ever") || "0"
      );
      const hasPoints =
        (quotesPoints > 0 || sopasPoints > 0) && !data.pointsMigrated;

      if (hasPoints) {
        setPendingMigration({
          token: data.token,
          quotes: quotesPoints,
          sopas: sopasPoints,
        });
      } else {
        navigate("/perfil");
      }
    } catch (err) {
      setError("Error de conexión, intenta de nuevo");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: "white",
    border: "1.5px solid #e0d0f0",
    borderRadius: "16px",
    padding: "12px 14px",
    fontSize: "14px",
    fontFamily: "Quicksand, sans-serif",
    outline: "none",
    width: "100%",
    color: "#2d1f3d",
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#b06ab3",
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };

  if (pendingMigration) {
    return (
      <MigrationPage
        token={pendingMigration.token}
        quotes={pendingMigration.quotes}
        sopas={pendingMigration.sopas}
        onDone={() => setPendingMigration(null)}
      />
    );
  }

  const headerLabel =
    mode === "login" ? "Bienvenida" : mode === "signup" ? "Únete" : "Recuperar";
  const headerTitle =
    mode === "login"
      ? "¡Hola de nuevo bb! 💙"
      : mode === "signup"
      ? "Crea tu cuenta ✨"
      : "¿Olvidaste tu contraseña? 🔑";

  return (
    <div
      className="flex flex-col font-quicksand"
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "repeat-y",
        backgroundSize: "100% auto",
        backgroundPosition: "top center",
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <div className="px-5 pt-6 pb-10 flex flex-col gap-5">
        {/* Header */}
        <div>
          <p
            style={{
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.1em",
              color: "#b06ab3",
              textTransform: "uppercase",
              marginBottom: "4px",
            }}
          >
            {headerLabel}
          </p>
          <h1
            style={{
              fontSize: "22px",
              fontWeight: 800,
              color: "#2d1f3d",
              lineHeight: 1.2,
            }}
          >
            {headerTitle}
          </h1>
          {mode === "signup" && (
            <p style={{ fontSize: "13px", color: "#9b7eaa", marginTop: "4px" }}>
              Para aparecer en el leaderboard y más
            </p>
          )}
          {mode === "forgot" && !forgotSent && (
            <p style={{ fontSize: "13px", color: "#9b7eaa", marginTop: "4px" }}>
              Te mandamos un link a tu correo
            </p>
          )}
        </div>

        {/* Card del form */}
        <div
          style={{
            background: "rgba(255,255,255,0.92)",
            borderRadius: "28px",
            padding: "24px 20px",
            boxShadow: "0 4px 24px rgba(176,106,179,0.1)",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* ── Estado post-envío de forgot ── */}
          {mode === "forgot" && forgotSent ? (
            forgotNoEmail ? (
              <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "12px" }}>
                <span style={{ fontSize: "36px" }}>😔</span>
                <p style={{ fontSize: "14px", fontWeight: 700, color: "#2d1f3d", margin: 0 }}>
                  No tenemos un correo para esta cuenta
                </p>
                <p style={{ fontSize: "12px", color: "#9b7eaa", lineHeight: 1.6, margin: 0 }}>
                  Sin correo guardado no podemos recuperar la contraseña. Puedes agregar uno desde tu perfil si recuerdas tu contraseña, o crear una cuenta nueva.
                </p>
                <button
                  onClick={() => switchMode("signup")}
                  style={{
                    background: "linear-gradient(135deg, #b06ab3, #c4b5fd)",
                    border: "none",
                    borderRadius: "999px",
                    padding: "13px",
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "white",
                    fontFamily: "Quicksand, sans-serif",
                    cursor: "pointer",
                  }}
                >
                  Crear cuenta nueva ✨
                </button>
              </div>
            ) : (
              <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "12px" }}>
                <span style={{ fontSize: "36px" }}>💌</span>
                <p style={{ fontSize: "14px", fontWeight: 700, color: "#2d1f3d", margin: 0 }}>
                  ¡Revisa tu correo!
                </p>
                <p style={{ fontSize: "12px", color: "#9b7eaa", lineHeight: 1.6, margin: 0 }}>
                  Te enviamos un link para cambiar tu contraseña. Expira en 1 hora.
                </p>
              </div>
            )
          ) : (
            <>
              {/* Username */}
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label style={labelStyle}>Username</label>
                <input
                  type="text"
                  placeholder="tu_username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  style={inputStyle}
                />
              </div>

              {/* Password — oculto en modo forgot */}
              {mode !== "forgot" && (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={labelStyle}>Contraseña</label>
                  <input
                    type="password"
                    placeholder="mínimo 6 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={inputStyle}
                  />
                  {mode === "login" && (
                    <p
                      onClick={() => switchMode("forgot")}
                      style={{
                        fontSize: "11px",
                        color: "#7BDCFB",
                        fontWeight: 700,
                        textAlign: "right",
                        cursor: "pointer",
                        marginTop: "2px",
                      }}
                    >
                      ¿Olvidaste tu contraseña?
                    </p>
                  )}
                </div>
              )}

              {/* Email — solo signup */}
              {mode === "signup" && (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={labelStyle}>
                    Correo
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#b06ab3",
                        fontWeight: 600,
                        background: "#f0e8f8",
                        padding: "2px 8px",
                        borderRadius: "999px",
                      }}
                    >
                      opcional
                    </span>
                  </label>
                  <input
                    type="email"
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={inputStyle}
                  />
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#c4b5fd",
                      lineHeight: 1.5,
                      marginTop: "2px",
                    }}
                  >
                    Solo por si olvidas tu contraseña bb, no te mandaremos spam 💜
                  </p>
                </div>
              )}

              {/* Error */}
              {error && (
                <p style={{ fontSize: "12px", color: "#f87171", margin: 0 }}>
                  {error}
                </p>
              )}

              {/* Botón submit */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  background: loading ? "#b0e8fb" : "#7BDCFB",
                  border: "none",
                  borderRadius: "999px",
                  padding: "14px",
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "white",
                  fontFamily: "Quicksand, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  marginTop: "4px",
                }}
              >
                {loading
                  ? "Un momento..."
                  : mode === "login"
                  ? "Entrar"
                  : mode === "signup"
                  ? "Crear cuenta"
                  : "Enviar link 💌"}
              </button>
            </>
          )}
        </div>

        {/* Switch mode */}
        <p style={{ fontSize: "13px", color: "#9b7eaa", textAlign: "center" }}>
          {mode === "forgot" ? (
            <>
              <span
                onClick={() => switchMode("login")}
                style={{ color: "#b06ab3", fontWeight: 700, cursor: "pointer" }}
              >
                ← Volver a iniciar sesión
              </span>
            </>
          ) : mode === "login" ? (
            <>
              ¿No tienes cuenta?{" "}
              <span
                onClick={() => switchMode("signup")}
                style={{ color: "#b06ab3", fontWeight: 700, cursor: "pointer" }}
              >
                Regístrate
              </span>
            </>
          ) : (
            <>
              ¿Ya tienes cuenta?{" "}
              <span
                onClick={() => switchMode("login")}
                style={{ color: "#b06ab3", fontWeight: 700, cursor: "pointer" }}
              >
                Inicia sesión
              </span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
